"use client"

import { Button, IconButton, Text } from "@/elements"
import { JobPostingStatus } from "@/gql/graphql"
import { useOutOfView } from "@/hooks/useOutOfView"
import type { NovaSession } from "@/types"
import { PlusIcon } from "@heroicons/react/24/solid"
import clsx from "clsx"
import { useSearchParams } from "next/navigation"
import { useRef } from "react"
import { ActiveJobsList } from "./activeJobs/ActiveJobList"
import { ClosedJobs } from "./closedJobs/ClosedJobs"
import { DraftJobs } from "./draftJobs/DraftJobs"
import { JOB_POST_STATUS_FILTER, MyJobPostFilters } from "./MyJobPostFilters"
import { useMyJobs } from "../hooks/useMyJobs"

interface Props {
  currentUserField: "client" | "creative"
  serverSession: NovaSession
}

export const MyJobPostsSection: React.FC<Props> = props => {
  const { serverSession } = props
  const searchParams = useSearchParams()
  const { setPostAJobModalOpen } = useMyJobs()
  const stickyRef = useRef<HTMLDivElement>(null)

  const statusFilter = searchParams?.get(JOB_POST_STATUS_FILTER) as JobPostingStatus | null
  const isSticky = useOutOfView(stickyRef, {
    threshold: 0.5,
  })

  return (
    <>
      <div className="flex flex-col justify-between px-5 sm:px-10 md:px-12">
        <div className="fixed bottom-6 right-5 z-20 block md:hidden">
          <IconButton
            iconType="filled"
            floating
            variant="primary"
            onClick={() => setPostAJobModalOpen(true)}
            Icon={PlusIcon}
            size="xl"
          />
        </div>
        <div className="flex flex-row justify-between pb-6 pt-10">
          <Text variant="h4">My job posts</Text>
          <div
            className={clsx("hidden transition-all duration-1000 md:block", isSticky ? "opacity-0" : "md:opacity-100")}>
            <Button onClick={() => setPostAJobModalOpen(true)}>Post a job</Button>
          </div>
        </div>
      </div>
      <div ref={stickyRef} className="absolute top-[16px] h-[0px] w-full md:top-[104px]" />
      <div className={clsx("sticky top-[72px] z-30 sm:top-[-1px]")}>
        <div
          className={clsx("flex flex-row items-center justify-between px-5 sm:px-10 md:px-12", isSticky && "bg-bg-1")}>
          <MyJobPostFilters serverSession={serverSession} />

          <div
            className={clsx(
              "pointer-events-none opacity-0 transition-all",
              isSticky ? "md:pointer-events-auto md:opacity-100" : "opacity-0"
            )}>
            <Button onClick={() => setPostAJobModalOpen(true)}> Post a job</Button>
          </div>
        </div>

        <div
          className={clsx("absolute bottom-0 left-0 h-[1px] w-full transition-all", {
            "bg-bg-3": isSticky,
          })}
        />
      </div>

      {statusFilter === JobPostingStatus.Closed ? (
        <div className="px-5 sm:px-10 md:px-12">
          <ClosedJobs currentUserField={props.currentUserField} />
        </div>
      ) : statusFilter === JobPostingStatus.Draft ? (
        <div className="px-5 sm:px-10 md:px-12">
          <DraftJobs currentUserField={props.currentUserField} serverSession={serverSession} />
        </div>
      ) : (
        <ActiveJobsList currentUserField={props.currentUserField} serverSession={serverSession} />
      )}
    </>
  )
}
